import { z } from 'zod';
import type {
  MerchantReceiptPeriod,
  MerchantReceiptsQueryParams,
  ReceiptStatusFilter,
} from './service/types';

const RECEIPT_PERIOD_OPTIONS: MerchantReceiptPeriod[] = ['today', '7d', '30d', '90d'];
const RECEIPT_STATUS_OPTIONS: ReceiptStatusFilter[] = ['pending', 'done', 'failed'];
const RECEIPTS_PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const DEFAULT_RECEIPTS_PAGE_SIZE = 20;
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const receiptsSearchSchema = z.object({
  // Legacy deep-link alias. Kept for backward compatibility and redirected to `q`.
  txid: z.string().optional(),
  q: z.string().optional(),
  status: z.string().optional(),
  has_image: z.union([z.string(), z.boolean()]).optional(),
  period: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  branch_id: z.string().optional(),
  page: z.union([z.string(), z.number()]).optional(),
  page_size: z.union([z.string(), z.number()]).optional(),
});

type HasImageSearchValue = 'true' | 'false';

export type ReceiptsSearchInput = z.infer<typeof receiptsSearchSchema>;

export interface ReceiptsRouteSearch {
  q?: string;
  status?: string;
  has_image?: string | boolean;
  period?: string;
  from?: string;
  to?: string;
  branch_id?: string;
  page?: string | number;
  page_size?: string | number;
}

export interface ReceiptsPageFilters {
  q?: string;
  status?: ReceiptStatusFilter;
  hasImage?: boolean;
  period?: MerchantReceiptPeriod;
  from?: string;
  to?: string;
  branchId?: string;
  page?: number;
  pageSize?: (typeof RECEIPTS_PAGE_SIZE_OPTIONS)[number];
}

function normalizePage(value: ReceiptsSearchInput['page']): number | undefined {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 1 ? value : undefined;
  }
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
}

function normalizePageSize(
  value: ReceiptsSearchInput['page_size'],
): (typeof RECEIPTS_PAGE_SIZE_OPTIONS)[number] | undefined {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value.trim())
        : undefined;
  if (!parsed || !Number.isInteger(parsed)) return undefined;
  if (!RECEIPTS_PAGE_SIZE_OPTIONS.includes(parsed as (typeof RECEIPTS_PAGE_SIZE_OPTIONS)[number])) {
    return undefined;
  }
  return parsed as (typeof RECEIPTS_PAGE_SIZE_OPTIONS)[number];
}

function normalizeHasImage(value: ReceiptsSearchInput['has_image']): HasImageSearchValue | undefined {
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim().toLowerCase();
  if (normalized === 'true' || normalized === '1') return 'true';
  if (normalized === 'false' || normalized === '0') return 'false';
  return undefined;
}

export function normalizeReceiptsSearch(search: ReceiptsSearchInput): ReceiptsRouteSearch {
  const normalizedQuery = search.q?.trim();
  const normalizedTxid = search.txid?.trim();
  const q = (normalizedQuery || normalizedTxid || '').slice(0, 255) || undefined;
  const status = RECEIPT_STATUS_OPTIONS.includes(search.status as ReceiptStatusFilter)
    ? (search.status as ReceiptStatusFilter)
    : undefined;
  const has_image = normalizeHasImage(search.has_image);
  const period = RECEIPT_PERIOD_OPTIONS.includes(search.period as MerchantReceiptPeriod)
    ? (search.period as MerchantReceiptPeriod)
    : undefined;
  const branch_id =
    search.branch_id && UUID_REGEX.test(search.branch_id) ? search.branch_id : undefined;
  const parsedPage = normalizePage(search.page);
  const parsedPageSize = normalizePageSize(search.page_size);
  const page = parsedPage && parsedPage > 1 ? parsedPage : undefined;
  const page_size =
    parsedPageSize && parsedPageSize !== DEFAULT_RECEIPTS_PAGE_SIZE ? parsedPageSize : undefined;

  const hasValidRange =
    Boolean(search.from) &&
    Boolean(search.to) &&
    !Number.isNaN(Date.parse(search.from!)) &&
    !Number.isNaN(Date.parse(search.to!)) &&
    Date.parse(search.from!) <= Date.parse(search.to!);

  return {
    q,
    status,
    has_image,
    period,
    from: hasValidRange ? search.from : undefined,
    to: hasValidRange ? search.to : undefined,
    branch_id,
    page,
    page_size,
  };
}

export function areReceiptsSearchEqual(a: ReceiptsSearchInput, b: ReceiptsRouteSearch) {
  return (
    typeof a.txid === 'undefined' &&
    a.q === b.q &&
    a.status === b.status &&
    a.has_image === b.has_image &&
    a.period === b.period &&
    a.from === b.from &&
    a.to === b.to &&
    a.branch_id === b.branch_id &&
    a.page === b.page &&
    a.page_size === b.page_size
  );
}

export function toReceiptsPageFilters(search: ReceiptsRouteSearch): ReceiptsPageFilters {
  return {
    q: search.q,
    status: search.status as ReceiptStatusFilter | undefined,
    hasImage:
      search.has_image === 'true' || search.has_image === true
        ? true
        : search.has_image === 'false' || search.has_image === false
          ? false
          : undefined,
    period: search.period as MerchantReceiptPeriod | undefined,
    from: search.from,
    to: search.to,
    branchId: search.branch_id,
    page: normalizePage(search.page),
    pageSize: normalizePageSize(search.page_size),
  };
}

export function toReceiptsRouteSearch(filters: ReceiptsPageFilters): ReceiptsRouteSearch {
  return {
    q: filters.q,
    status: filters.status,
    has_image: filters.hasImage === undefined ? undefined : String(filters.hasImage),
    period: filters.period,
    from: filters.from,
    to: filters.to,
    branch_id: filters.branchId,
    page: filters.page && filters.page > 1 ? filters.page : undefined,
    page_size:
      filters.pageSize && filters.pageSize !== DEFAULT_RECEIPTS_PAGE_SIZE ? filters.pageSize : undefined,
  };
}

export function receiptsLoaderDeps(search: ReceiptsSearchInput): MerchantReceiptsQueryParams {
  const normalized = normalizeReceiptsSearch(search);

  return {
    q: normalized.q,
    status: normalized.status as ReceiptStatusFilter | undefined,
    has_image:
      normalized.has_image === 'true'
        ? true
        : normalized.has_image === 'false'
          ? false
          : undefined,
    period: normalized.period as MerchantReceiptPeriod | undefined,
    from: normalized.from,
    to: normalized.to,
    branch_id: normalized.branch_id,
    page: normalizePage(normalized.page) ?? 1,
    page_size: normalizePageSize(normalized.page_size) ?? DEFAULT_RECEIPTS_PAGE_SIZE,
  };
}

export { DEFAULT_RECEIPTS_PAGE_SIZE, RECEIPTS_PAGE_SIZE_OPTIONS };
